<template>
  <panel class="battle-scene" width="100%" height="100%">

    <!-- Player HUD -->

    <panel top="5px" left="5px" position="absolute">
      <flex direction="column">
        <health-bar>
          {{ player.name.toUpperCase() }}
          [{{ battle.player.health }}/20]
        </health-bar>
        <panel padding="18px 0 0 29px">
          <character />
        </panel>
      </flex>
    </panel>

    <!-- Enemy HUD -->

    <panel top="5px" right="5px" position="absolute">
      <flex direction="column" align="flex-end">
        <health-bar align="right">
          <div style="margin-right:-1px">
            [{{ battle.enemy.health }}/20]
            {{ enemy.name.toUpperCase() }}
          </div>
        </health-bar>
        <panel padding="18px 29px 0 0">
          <character />
        </panel>
      </flex>
    </panel>

    <!-- Text -->

    <panel-transition
      ref="textPanelTx"
      :to="{ translateY: '0px' }"
      :from="{ translateY: '34px', elasticity: 0 }"
      :visible="false"
    >
      <template slot-scope="slotProps">
        <panel v-if="slotProps.visible" z="1" left="0" right="0" bottom="0" position="absolute" style="height:32px">
          <text-drawer ref="textDrawer" />
        </panel>
      </template>
    </panel-transition>

    <!-- Battle UI -->

    <panel z="2" left="0" right="0" bottom="0" position="absolute" style="height:11px">
      <!-- Attack Selection -->
      <animeta-transition
        :to="{ translateX: '0px' }"
        :from="{ translateX: '-61px', elasticity: 0 }"
      >
          <panel v-if="showAttack" z="1" bottom="4px" left="5px" position="absolute">
            <icon-group
              :line-top="selectedElement.name"
              :line-bottom="selectedAttack.name"
              @select="selectedAttackId = $event"
            >
              <icon-group-radio v-if="player.attacks['0'] !== undefined" index="0" :selected="selectedAttackId === '0'" />
              <icon-group-radio v-if="player.attacks['1'] !== undefined" index="1" :selected="selectedAttackId === '1'" />
              <icon-group-radio v-if="player.attacks['2'] !== undefined" index="2" :selected="selectedAttackId === '2'" />
            </icon-group>
          </panel>
      </animeta-transition>

      <!-- Attack Button -->
      <animeta-transition
        :to="{ translateX: '0px' }"
        :from="{ translateX: '74px', elasticity: 0 }"
      >
        <panel v-if="showAttack" z="2" top="-19px" right="5px" position="absolute">
          <btn @click="sendAction">Attack</btn>
        </panel>
      </animeta-transition>

      <!-- Background Drawer -->
      <animeta-transition
        :to="{ translateY: '0px' }"
        :from="{ translateY: '13px', elasticity: 0 }"
      >
        <drawer v-if="showAttack" style="z-index:0;height:22px" />
      </animeta-transition>
    </panel>

  </panel>
</template>

<script>
import animeta from 'vue-animeta'

import attacks from '@/game/attacks.js'
import elements from '@/game/elements.js'
import BattleScene from '@/game/BattleScene.js'

// Components
import Btn from '@/components/Btn.vue'
import Icon from '@/components/Icon.vue'
import Flex from '@/components/Flex.vue'
import Panel from '@/components/Panel.vue'
import Drawer from '@/components/Drawer.vue'
import Character from '@/components/Character.vue'
import HealthBar from '@/components/HealthBar.vue'
import IconGroup from '@/components/IconGroup.vue'
import TextDrawer from '@/components/TextDrawer.vue'
import IconGroupRadio from '@/components/IconGroupRadio.vue'
import PanelTransition from '@/components/PanelTransition.vue'

export default {
  name: 'BattleScene',

  components: {
    ...animeta,

    Btn,
    Icon,
    Flex,
    Panel,
    Drawer,
    Character,
    HealthBar,
    IconGroup,
    TextDrawer,
    IconGroupRadio,
    PanelTransition
  },

  data () {
    const player = {
      id: '0',
      name: localStorage.getItem('playerName') || prompt('What\'s your name?'),
      race: 0,
      item: 0,
      level: 0,
      cards: [],
      attacks: {
        0: '0',
        1: '1',
        2: '2'
      }
    }
    if (!player.name) return location.reload()
    localStorage.setItem('playerName', player.name)

    const enemy = {
      id: '1',
      name: 'Bad Dood',
      race: 0,
      item: 0,
      level: 0,
      cards: [],
      attacks: {
        0: '0',
        1: '2',
        2: undefined
      }
    }

    const battle = new BattleScene(
      player,
      enemy,
      this.onEmit.bind(this)
    )

    return {
      queue: [],
      battle,
      characters: {
        '0': player,
        '1': enemy
      },
      showAttack: true,
      showTextDrawer: false,
      selectedAttackId: '0'
    }
  },

  computed: {
    player () {
      return this.characters['0']
    },

    enemy () {
      return this.characters['1']
    },

    selectedAttack () {
      const { selectedAttackId: id } = this
      return (id && attacks[id]) || {}
    },

    selectedElement () {
      const { selectedAttack } = this
      return (selectedAttack && elements[selectedAttack.element]) || {}
    }
  },

  methods: {
    onEmit (evt) {
      let textPanelVisible = this.$refs.textPanelTx.reallyVisible

      if (evt.name === 'ATTACK_QUEUE_RESOLVED') {
        evt.payload.forEach((action, i) => {
          const attacker = action.attacker && this.characters[action.attacker]
          const defender = action.defender && this.characters[action.defender]

          // Who goes first

          if (!textPanelVisible) {
            this.queue.push({
              name: 'TEXT_SHOW'
            })
            textPanelVisible = true
          }

          if (i === 0) {
            const charName = this.characters[action.attacker].name.toUpperCase()
            this.queue.push({
              name: 'TEXT',
              payload: {
                text: `${charName} goes first.`
              }
            })
          }

          // Action

          if (action.name === 'DAMAGE_DONE') {
            const attack = attacks[action.attack]

            this.queue.push({
              name: 'TEXT',
              payload: {
                text: `${attacker.name.toUpperCase()} uses ${attack.name.toUpperCase()}!`
              }
            })

            this.queue.push({
              name: 'TEXT_HIDE'
            })
            textPanelVisible = false

            this.queue.push({
              name: 'ATTACK',
              payload: {
                attack: action.attack,
                attacker: action.attacker,
                defender: action.defender
              }
            })

            this.queue.push({
              name: 'TEXT_SHOW'
            })
            textPanelVisible = true

            this.queue.push({
              name: 'TEXT',
              payload: {
                text: `${defender.name.toUpperCase()} takes ${Math.round(action.damage)} damage.`
              }
            })
          }

          if (action.name === 'EVADED' || action.name === 'MISSED') {
            const attack = attacks[action.attack]

            this.queue.push({
              name: 'TEXT',
              payload: {
                text: `${attacker.name.toUpperCase()} uses ${attack.name.toUpperCase()}!`
              }
            })
          }

          if (action.name === 'EVADED') {
            this.queue.push({
              name: 'TEXT',
              payload: {
                text: `${defender.name.toUpperCase()} evaded the attack!`
              }
            })
          }

          if (action.name === 'MISSED') {
            this.queue.push({
              name: 'TEXT',
              payload: {
                text: `${attacker.name.toUpperCase()} missed!`
              }
            })
          }

          // Win / lose

          if (action.name === 'CHARACTER_DEFEATED') {
            const charName = this.characters[action.loser].name.toUpperCase()
            this.queue.push({
              name: 'TEXT',
              payload: {
                text: `${charName} has been defeated!`
              }
            })

            if (action.loser === this.player.id) {
              this.queue.push({
                name: 'LOSE'
              })
            } else {
              this.queue.push({
                name: 'WIN'
              })
            }
          }

          // Turn over

          else if (i === evt.payload.length - 1) {
            this.queue.push({ name: 'NEXT_ROUND' })
          }
        })

        this.runQueue()
      }
    },

    runQueue () {
      if (this.queue.length === 0) return
      console.log('running queue...')

      const next = () => {
        console.log('next')
        this.runQueue()
      }
      const item = this.queue.shift()

      console.log(item)

      // Text

      if (item.name === 'TEXT_SHOW') {
        this.$refs.textPanelTx.show(next)
      }

      if (item.name === 'TEXT_HIDE') {
        this.$refs.textPanelTx.hide(next)
      }

      if (item.name === 'TEXT') {
        console.log(item.payload.text)
        this.$refs.textDrawer.showText(item.payload.text, next)
      }

      // Attack

      if (item.name === 'ATTACK') {
        console.log('ATTACK happened')
        next()
      }

      // Next round

      if (item.name === 'NEXT_ROUND') {
        this.queue = []
        this.$refs.textPanelTx.hide(next)
        this.showAttack = true
      }

      // Win

      if (item.name === 'WIN') {
        this.$refs.textPanelTx.hide(() => {
          alert('You win!')
          this.$emit('win')
        })
      }

      // Lose

      if (item.name === 'LOSE') {
        this.$refs.textPanelTx.hide(() => {
          alert('Game Over :(')
          this.$emit('lose')
        })
      }
    },

    sendAction () {
      if (this.selectedAttackId === undefined) return

      this.showAttack = false

      this.battle.sendAction(this.player.id, this.selectedAttackId)
      this.battle.sendAction(this.enemy.id, Math.round(Math.random()).toString())
    }
  }
}
</script>

<style lang="sass" scoped>
.battle-scene
  overflow: hidden
</style>
